import type { CSSProperties } from 'react';

import type { MapWorkbenchSpiderifyPoint } from './spiderify';

export interface MapWorkbenchAnchoredOverlayViewport {
  width: number;
  height: number;
}

export interface MapWorkbenchAnchoredOverlayPlacementOptions {
  anchor?: MapWorkbenchSpiderifyPoint | null;
  viewport?: MapWorkbenchAnchoredOverlayViewport | null;
  preferredWidth: number;
  preferredHeight: number;
  offset?: number;
  padding?: number;
  fallback?: CSSProperties;
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

export const buildMapWorkbenchAnchoredOverlayStyle = ({
  anchor,
  viewport,
  preferredWidth,
  preferredHeight,
  offset = 12,
  padding = 12,
  fallback = { left: '12px', bottom: '12px' },
}: MapWorkbenchAnchoredOverlayPlacementOptions): CSSProperties => {
  if (!anchor || !viewport || viewport.width <= 0 || viewport.height <= 0) {
    return fallback;
  }

  const rightSpace = viewport.width - anchor.x - offset - padding;
  const leftSpace = anchor.x - offset - padding;
  const bottomSpace = viewport.height - anchor.y - offset - padding;
  const topSpace = anchor.y - offset - padding;

  const placeRight = rightSpace >= preferredWidth || rightSpace >= leftSpace;
  const placeBelow = bottomSpace >= preferredHeight || bottomSpace >= topSpace;

  const style: CSSProperties = {};

  if (placeRight) {
    style.left = `${clamp(anchor.x + offset, padding, Math.max(padding, viewport.width - padding))}px`;
  } else {
    style.right = `${clamp(viewport.width - anchor.x + offset, padding, Math.max(padding, viewport.width - padding))}px`;
  }

  if (placeBelow) {
    style.top = `${clamp(anchor.y + offset, padding, Math.max(padding, viewport.height - padding))}px`;
  } else {
    style.bottom = `${clamp(viewport.height - anchor.y + offset, padding, Math.max(padding, viewport.height - padding))}px`;
  }

  return style;
};
